/**
 * ADD ITEMS MODAL
 * 
 * Allows users to add more items to an existing conversation.
 * Features:
 * - Toggle between "Their Items" (other user's) and "My Items" (own items)
 * - Destash items tab
 * - Creations tab (press-ons, jewelry, etc.)
 * - Sellers can suggest their own items (marked as "Suggested by seller")
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { addItemsToConversation, addCreationsToConversation } from '../../messaging/messagingService';
import type { AttachedItem, AttachedCreation } from '../../messaging/types';
import type { CollectionItem } from '../../types';
import type { Creation } from '../../types/creationTypes';
import { X, Plus, Loader2, Check, Search, Store, Sparkles, Users, User } from 'lucide-react';
import { AppIcons } from '../../constants/icons';

interface AddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  currentUserId: string;
  otherUserId: string;
  existingItemIds: string[]; // Items already in conversation
  existingCreationIds?: string[]; // Creations already in conversation
  onItemsAdded?: () => void;
}

type TabType = 'destash' | 'creations';
type SourceType = 'theirs' | 'mine';

const AddItemsModal: React.FC<AddItemsModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  currentUserId,
  otherUserId,
  existingItemIds,
  existingCreationIds = [],
  onItemsAdded
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('destash');
  const [source, setSource] = useState<SourceType>('theirs');
  
  // Other user's items state
  const [theirItems, setTheirItems] = useState<CollectionItem[]>([]);
  const [loadingTheirItems, setLoadingTheirItems] = useState(true);
  
  // Current user's items state
  const [myItems, setMyItems] = useState<CollectionItem[]>([]);
  const [loadingMyItems, setLoadingMyItems] = useState(true);
  
  // Selected items (across both sources)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  
  // Other user's creations state
  const [theirCreations, setTheirCreations] = useState<Creation[]>([]);
  const [loadingTheirCreations, setLoadingTheirCreations] = useState(true);
  
  // Current user's creations state
  const [myCreations, setMyCreations] = useState<Creation[]>([]);
  const [loadingMyCreations, setLoadingMyCreations] = useState(true);
  
  // Selected creations
  const [selectedCreations, setSelectedCreations] = useState<Set<string>>(new Set());
  
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch other user's destash items
  useEffect(() => {
    if (!isOpen || !otherUserId) return;

    const fetchItems = async () => {
      setLoadingTheirItems(true);
      try {
        const itemsRef = collection(db, 'users', otherUserId, 'items');
        const q = query(
          itemsRef,
          where('isForDestash', '==', true)
        );
        
        const snapshot = await getDocs(q);
        const fetchedItems: CollectionItem[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data() as CollectionItem;
          // Only include items not already in conversation and not sold
          if (!existingItemIds.includes(doc.id) && data.destashStatus !== 'sold') {
            fetchedItems.push({
              ...data,
              id: doc.id
            });
          }
        });
        
        setTheirItems(fetchedItems);
      } catch (err) {
        console.error('Error fetching their items:', err);
      } finally {
        setLoadingTheirItems(false);
      }
    };

    fetchItems();
  }, [isOpen, otherUserId, existingItemIds]);

  // Fetch current user's destash items
  useEffect(() => {
    if (!isOpen || !currentUserId) return;

    const fetchMyItems = async () => {
      setLoadingMyItems(true);
      try {
        const itemsRef = collection(db, 'users', currentUserId, 'items');
        const q = query(
          itemsRef,
          where('isForDestash', '==', true)
        );
        
        const snapshot = await getDocs(q);
        const fetchedItems: CollectionItem[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data() as CollectionItem;
          // Only include items not already in conversation and not sold
          if (!existingItemIds.includes(doc.id) && data.destashStatus !== 'sold') {
            fetchedItems.push({
              ...data,
              id: doc.id
            });
          }
        });
        
        setMyItems(fetchedItems);
      } catch (err) {
        console.error('Error fetching my items:', err);
      } finally {
        setLoadingMyItems(false);
      }
    };

    fetchMyItems();
  }, [isOpen, currentUserId, existingItemIds]);

  // Fetch other user's marketplace creations
  useEffect(() => {
    if (!isOpen || !otherUserId) return;

    const fetchCreations = async () => {
      setLoadingTheirCreations(true);
      try {
        const creationsRef = collection(db, 'users', otherUserId, 'creations');
        const q = query(
          creationsRef,
          where('listedOnMarketplace', '==', true)
        );
        
        const snapshot = await getDocs(q);
        const fetchedCreations: Creation[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data() as Creation;
          // Only include creations not already in conversation and not sold
          if (!existingCreationIds.includes(doc.id) && data.marketplaceStatus !== 'sold') {
            fetchedCreations.push({
              ...data,
              id: doc.id
            });
          }
        });
        
        setTheirCreations(fetchedCreations);
      } catch (err) {
        console.error('Error fetching their creations:', err);
      } finally {
        setLoadingTheirCreations(false);
      }
    };

    fetchCreations();
  }, [isOpen, otherUserId, existingCreationIds]);

  // Fetch current user's marketplace creations
  useEffect(() => {
    if (!isOpen || !currentUserId) return;

    const fetchMyCreations = async () => {
      setLoadingMyCreations(true);
      try {
        const creationsRef = collection(db, 'users', currentUserId, 'creations');
        const q = query(
          creationsRef,
          where('listedOnMarketplace', '==', true)
        );
        
        const snapshot = await getDocs(q);
        const fetchedCreations: Creation[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data() as Creation;
          // Only include creations not already in conversation and not sold
          if (!existingCreationIds.includes(doc.id) && data.marketplaceStatus !== 'sold') {
            fetchedCreations.push({
              ...data,
              id: doc.id
            });
          }
        });
        
        setMyCreations(fetchedCreations);
      } catch (err) {
        console.error('Error fetching my creations:', err);
      } finally {
        setLoadingMyCreations(false);
      }
    };

    fetchMyCreations();
  }, [isOpen, currentUserId, existingCreationIds]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedItems(new Set());
      setSelectedCreations(new Set());
      setSearchTerm('');
      setActiveTab('destash');
      setSource('theirs');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get items based on source
  const items = source === 'theirs' ? theirItems : myItems;
  const loadingItems = source === 'theirs' ? loadingTheirItems : loadingMyItems;
  const creations = source === 'theirs' ? theirCreations : myCreations;
  const loadingCreations = source === 'theirs' ? loadingTheirCreations : loadingMyCreations;

  // Filter items by search
  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter creations by search
  const filteredCreations = creations.filter(creation =>
    creation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    creation.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle item selection
  const toggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Toggle creation selection
  const toggleCreation = (creationId: string) => {
    const newSelected = new Set(selectedCreations);
    if (newSelected.has(creationId)) {
      newSelected.delete(creationId);
    } else {
      newSelected.add(creationId);
    }
    setSelectedCreations(newSelected);
  };

  // Get display image for an item
  const getItemDisplayImage = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    return null;
  };

  // Get display image for a creation
  const getCreationDisplayImage = (creation: Creation): string | null => {
    if (creation.photos && creation.photos.length > 0) {
      const defaultPhoto = creation.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : creation.photos[0].url;
    }
    return null;
  };

  // Get category label
  const getCategoryLabel = (category?: string) => {
    switch (category) {
      case 'pressons': return 'Press-Ons';
      case 'jewelry': return 'Jewelry';
      case 'other': return 'Other';
      default: return 'Creation';
    }
  };

  // Get total selected count
  const totalSelected = selectedItems.size + selectedCreations.size;

  // Add selected items/creations to conversation
  const handleAddItems = async () => {
    if (totalSelected === 0 || adding) return;

    setAdding(true);

    try {
      // Determine owner and suggestedBy based on source
      const ownerId = source === 'theirs' ? otherUserId : currentUserId;
      const suggestedBy = source === 'mine' ? 'seller' : undefined;

      // Add destash items if any selected
      if (selectedItems.size > 0) {
        const itemsToAdd: AttachedItem[] = items
          .filter(item => selectedItems.has(item.id))
          .map(item => {
            const attached: AttachedItem = {
              itemId: item.id,
              ownerId: ownerId,
              name: item.name,
              brand: item.brand,
              status: item.destashStatus || 'available',
            };
            if (suggestedBy) attached.suggestedBy = suggestedBy;
            const photoUrl = getItemDisplayImage(item);
            if (photoUrl) attached.photoUrl = photoUrl;
            return attached;
          });

        await addItemsToConversation(conversationId, currentUserId, itemsToAdd);
      }

      // Add creations if any selected
      if (selectedCreations.size > 0) {
        const creationsToAdd: AttachedCreation[] = creations
          .filter(creation => selectedCreations.has(creation.id))
          .map(creation => {
            const attached: AttachedCreation = {
              creationId: creation.id,
              ownerId: ownerId,
              title: creation.title,
              status: creation.marketplaceStatus || 'available',
            };
            if (suggestedBy) attached.suggestedBy = suggestedBy;
            if (creation.marketplaceCategory) attached.category = creation.marketplaceCategory;
            const photoUrl = getCreationDisplayImage(creation);
            if (photoUrl) attached.photoUrl = photoUrl;
            if (creation.price) attached.price = creation.price;
            return attached;
          });

        await addCreationsToConversation(conversationId, currentUserId, creationsToAdd);
      }
      
      onItemsAdded?.();
      onClose();
    } catch (err) {
      console.error('Error adding items:', err);
      alert('Failed to add items. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-modal max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <Plus className="icon-md text-glass" />
            <h2 className="font-bold text-lg text-glass font-body">
              Add to Conversation
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/60 rounded-full transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>

        {/* Source Toggle - Their Items vs My Items */}
        <div className="flex border-b border-slate-600 bg-slate-800/40">
          <button
            onClick={() => {
              setSource('theirs');
              setSelectedItems(new Set());
              setSelectedCreations(new Set());
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-medium transition-default font-body ${
              source === 'theirs'
                ? 'text-glass bg-slate-700/60 border-b-2 border-slate-400'
                : 'text-glass-muted hover:text-glass-secondary'
            }`}
          >
            <Users className="icon-sm" />
            Their Items
          </button>
          <button
            onClick={() => {
              setSource('mine');
              setSelectedItems(new Set());
              setSelectedCreations(new Set());
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-medium transition-default font-body ${
              source === 'mine'
                ? 'text-glass bg-slate-700/60 border-b-2 border-slate-400'
                : 'text-glass-muted hover:text-glass-secondary'
            }`}
          >
            <User className="icon-sm" />
            My Items
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
              Suggest
            </span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-600">
          <button
            onClick={() => setActiveTab('destash')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium transition-default font-body ${
              activeTab === 'destash'
                ? 'text-glass border-b-2 border-slate-400 bg-slate-700/40'
                : 'text-glass-muted hover:text-glass-secondary hover:bg-slate-700/40'
            }`}
          >
            <Store className="icon-sm" />
            Destash Items
            {items.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'destash' ? 'bg-slate-600 text-glass' : 'bg-slate-700 text-glass-secondary'
              }`}>
                {items.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('creations')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium transition-default font-body ${
              activeTab === 'creations'
                ? 'text-glass border-b-2 border-slate-400 bg-slate-700/40'
                : 'text-glass-muted hover:text-glass-secondary hover:bg-slate-700/40'
            }`}
          >
            <Sparkles className="icon-sm" />
            Creations
            {creations.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'creations' ? 'bg-slate-600 text-glass' : 'bg-slate-700 text-glass-secondary'
              }`}>
                {creations.length}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-600/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-muted" />
            <input
              type="text"
              placeholder={activeTab === 'destash' ? "Search items..." : "Search creations..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-glass pl-9 text-sm font-body"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Destash Items Tab */}
          {activeTab === 'destash' && (
            <>
              {loadingItems ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-glass-secondary animate-spin mx-auto mb-2" />
                  <p className="text-glass-muted text-sm font-body">
                    Loading items...
                  </p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-glass-muted font-body">
                    {searchTerm
                      ? 'No items match your search'
                      : 'No destash items available to add'
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredItems.map((item) => {
                    const isSelected = selectedItems.has(item.id);
                    const imageUrl = getItemDisplayImage(item);
                    
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        className={`relative glass-card-sm rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          isSelected
                            ? 'border-slate-400 ring-2 ring-slate-500'
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {/* Image */}
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.name}
                            className="w-full h-24 object-cover"
                          />
                        ) : (
                          <div className="w-full h-24 rainbow-gradient flex items-center justify-center">
                            <AppIcons.Polish className="w-8 h-8 text-current" aria-label="Nail Polish" />
                          </div>
                        )}

                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-slate-500 rounded-full flex items-center justify-center">
                            <Check className="icon-sm text-white" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="p-2">
                          <p className="text-xs text-glass-secondary font-bold truncate font-heading">
                            {item.brand}
                          </p>
                          <p className="text-sm font-bold text-glass truncate font-body">
                            {item.name}
                          </p>
                          {item.destashPrice && (
                            <p className="text-xs text-green-400 font-bold mt-1 font-body">
                              ${item.destashPrice}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Creations Tab */}
          {activeTab === 'creations' && (
            <>
              {loadingCreations ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-glass-secondary animate-spin mx-auto mb-2" />
                  <p className="text-glass-muted text-sm font-body">
                    Loading creations...
                  </p>
                </div>
              ) : filteredCreations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-glass-muted font-body">
                    {searchTerm
                      ? 'No creations match your search'
                      : 'No creations available to add'
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredCreations.map((creation) => {
                    const isSelected = selectedCreations.has(creation.id);
                    const imageUrl = getCreationDisplayImage(creation);
                    
                    return (
                      <div
                        key={creation.id}
                        onClick={() => toggleCreation(creation.id)}
                        className={`relative glass-card-sm rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          isSelected
                            ? 'border-slate-400 ring-2 ring-slate-500'
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {/* Image */}
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={creation.title}
                            className="w-full h-24 object-cover"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                            <AppIcons.Powder className="w-8 h-8 text-current" aria-label="Creation" />
                          </div>
                        )}

                        {/* Category badge */}
                        <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold rounded-full bg-purple-500/80 text-white">
                          {getCategoryLabel(creation.marketplaceCategory)}
                        </span>

                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-slate-500 rounded-full flex items-center justify-center">
                            <Check className="icon-sm text-white" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="p-2">
                          <p className="text-sm font-bold text-glass truncate font-body">
                            {creation.title}
                          </p>
                          {creation.price && (
                            <p className="text-xs text-green-400 font-bold mt-1 font-body">
                              ${creation.price.toFixed(2)}
                            </p>
                          )}
                          {creation.contactForPricing && !creation.price && (
                            <p className="text-xs text-glass-muted mt-1 font-body">
                              Contact for price
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-slate-600 bg-slate-800/40">
          <p className="text-sm text-glass-muted font-body">
            {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
            {selectedItems.size > 0 && selectedCreations.size > 0 && (
              <span className="text-xs text-glass-muted ml-1">
                ({selectedItems.size} destash, {selectedCreations.size} creation{selectedCreations.size !== 1 ? 's' : ''})
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-glass-secondary hover:bg-slate-700/60 rounded-lg transition-default font-body"
              disabled={adding}
            >
              Cancel
            </button>
            <button
              onClick={handleAddItems}
              disabled={totalSelected === 0 || adding}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-default font-body ${
                totalSelected > 0 && !adding
                  ? 'btn-primary'
                  : 'bg-slate-600 text-glass-muted cursor-not-allowed'
              }`}
            >
              {adding ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="icon-sm" />
                  Add to Chat
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemsModal;
